<script lang="ts" setup>
import { ContextType } from '@/graphql/types-service';
import { AdminUserContext } from '@/types';
import { parseInfraId } from '@/utils/customer';

withDefaults(
    defineProps<{
        slim?: boolean;
    }>(),
    {
        slim: false
    }
);

const session = useSessionStore();
const router = useRouter();
const route = useRoute();
const sub = ref<AdminUserContext | null>(null);
const subActive = ref<boolean>(false);

async function onClose() {
    onCloseSub();
    sessionStorage.setItem('ra', route.fullPath);
    await nextTick();
    session.selectedContext.clear();
    await nextTick();
}

function onSelectSub() {
    if (sub.value) {
        const id = parseInfraId(sub.value.id);
        switch (id?._idType) {
            case 'T':
            case 'R': {
                const federalState = session.federalStateList.find(
                    (it) => it.id._id === (id as any)?._cid
                );
                if (federalState) {
                    session.selectedContext.setRoot(federalState);
                }
                break;
            }
            default:
                break;
        }
        session.selectedContext.setSub(sub.value);
    } else {
        session.selectedContext.clearSub();
    }
}

async function onCloseSub() {
    session.selectedContext.clearSub();
    sub.value = null;
    subActive.value = false;
    await new Promise((res) => setTimeout(res, 700));
    if (route.fullPath.startsWith('/watersupplier/details')) {
        router.replace('/watersupplier');
    }
}

function addSubContext() {
    subActive.value = true;
}

async function addContext() {
    sessionStorage.setItem('ra', route.fullPath);
    await nextTick();
    router.push('/context');
}
const layoutStore = useLayoutStore();
const el = useTemplateRef('admin_context');

useResizeObserver(el, (entries) => {
    if (entries && entries.length > 0) {
        const entry = entries[0];
        const { height } = entry.contentRect;
        layoutStore.setContextHeight(height);
    }
});
</script>
<template>
    <SimpleCard
        v-if="session.sessionContext.isAdminOrSupport"
        ref="admin_context"
        data-testid="admin-context-selection"
    >
        <SimpleCardTitle>
            {{ $t('ux.labels.selected_context') }}
        </SimpleCardTitle>
        <Divider />
        <Message
            v-if="session.selectedContext.rootId"
            class="mb-4"
            severity="info"
            closable
            @close="onClose"
        >
            {{ session.selectedContext.root?.name }}
        </Message>
        <Button
            v-else
            class="w-full"
            icon="pi pi-plus"
            text
            @click="addContext"
        />
        <template v-if="!slim && session.selectedContext.rootId">
            <template v-if="session.selectedContext.subId">
                <Message
                    class="mb-4"
                    severity="success"
                    closable
                    @close="onCloseSub"
                >
                    {{ session.selectedContext.sub?.name }}
                </Message>
                <div
                    v-if="
                        session.contextKind &&
                        session.contextType === ContextType.Hdp
                    "
                    class="text-center text-xs font-semibold pb-1"
                >
                    {{
                        $t(
                            `dx.${session.contextType}_${session.contextKind}_help_text`.toLowerCase(),
                            {
                                state: $t(`dx.state.${session.federalStateKey}`)
                            }
                        )
                    }}
                </div>
                <div v-else class="text-center text-xs font-semibold pb-1">
                    {{
                        $t(
                            `dx.${session.contextType}_help_text`.toLowerCase(),
                            {
                                state: $t(`dx.state.${session.federalStateKey}`)
                            }
                        )
                    }}
                </div>
            </template>

            <AdminContextSearch
                v-else-if="subActive"
                v-model="sub"
                :context="session.selectedContext.rootId.toString()"
                @select="onSelectSub"
                @close="onCloseSub"
            />
            <Button
                v-else
                data-testid="addSubContext"
                class="w-full"
                icon="pi pi-plus"
                text
                @click="addSubContext"
            />
        </template>
    </SimpleCard>
</template>
